import _, { isFunction } from 'lodash';
import { t } from '@lingui/macro';
import { i18n } from '@lingui/core';
import { useCallback, useMemo } from 'react';

import { DEFAULT_VALIDATION_ERROR_TEXT, errorAlertObject } from '../components/form/FormConstants';
import useUiAlerts, { createAlertConfig, ERROR, WARNING } from '../stores/AlertsStore';
import { HTTP_STATUS_400 } from './ApiDataService';
import { MANUAL_ERROR_TYPE, unpackValidationErrors } from '../utilities/FormUtilities';
import { useNavigate } from '../utilities/PgmUtilities';

export const GENERIC_SERVER_ERROR = t`An error occurred while communicating with the server.  Please contact support.`;
const genericServerErrorAlert = createAlertConfig(ERROR, i18n._(GENERIC_SERVER_ERROR));

export function useErrorWrapper() {
    const [, uiAlertActions] = useUiAlerts();
    const navigate = useNavigate();

    const navigateAway = useMemo(() => {
        return (goTo) => {
            if (_.isEmpty(goTo)) {
                return;
            }

            return navigate(goTo, { replace: true });
        };
    }, [navigate]);

    const errorWrapper = useMemo(() => {
        return (createAlertConfigFunction, onErrorGoTo) => {
            return (errorObject) => {
                const alertConfig = _.isFunction(createAlertConfigFunction)
                    ? createAlertConfigFunction(errorObject)
                    : genericServerErrorAlert;

                // eslint-disable-next-line no-console
                console.error('useErrorWrapper Error', errorObject.responseBody || errorObject);

                uiAlertActions.createAlert(_.extend(alertConfig, { createOnNavigate: !_.isEmpty(onErrorGoTo) }));
                navigateAway(onErrorGoTo);
            };
        };
    }, [navigateAway, uiAlertActions]);

    return {
        errorWrapper,
    };
}

const getErrorMsg = (responseErrorMessage, errorObject) => {
    if (!_.isEmpty(responseErrorMessage)) {
        return responseErrorMessage;
    }

    const responseBody = _.get(errorObject, 'responseBody');
    if (_.isEmpty(responseBody)) {
        return responseBody;
    }

    if (_.isString(responseBody)) {
        return responseBody;
    }

    const httpCode = _.get(responseBody, 'httpCode');
    if (HTTP_STATUS_400 === httpCode) {
        const responseBodyMessage = _.get(responseBody, 'message');
        if (!_.isEmpty(responseBodyMessage)) {
            return responseBodyMessage;
        }

        const detailsMessage = _.get(responseBody, 'details.message');
        if (!_.isEmpty(detailsMessage)) {
            return detailsMessage;
        }

        const userResponseMessage = _.get(responseBody, 'userMessage');
        if (!_.isEmpty(userResponseMessage)) {
            return userResponseMessage;
        }
    }

    const message = _.get(errorObject, 'message');
    if (!_.isEmpty(responseBody)) {
        return message;
    }

    const responseBodyMessage = _.get(responseBody, 'message');
    if (!_.isEmpty(responseBodyMessage)) {
        return responseBodyMessage;
    }
    return undefined;
};
export function useGenericError() {
    const [, uiAlertActions] = useUiAlerts();

    return useCallback(
        (errorObject) => {
            let alertConfig = errorAlertObject;
            const responseErrorMessage = _.get(errorObject, 'responseErrorMessage');
            if (
                HTTP_STATUS_400 === errorObject.getStatus?.() ||
                HTTP_STATUS_400 === _.get(errorObject, 'responseBody.httpCode') ||
                !_.isEmpty(responseErrorMessage)
            ) {
                const responseText = getErrorMsg(responseErrorMessage, errorObject);
                const alertText = _.isString(responseText) ? responseText : DEFAULT_VALIDATION_ERROR_TEXT;
                alertConfig = createAlertConfig(WARNING, alertText);
            }

            // We may have handled errors at this point, but leaving here in case we want
            // to separate error handing.
            uiAlertActions.createAlert(alertConfig);

            // eslint-disable-next-line no-console
            console.error('useGenericError Error', errorObject.responseBody || errorObject);
        },
        [uiAlertActions]
    );
}

export function useServerError() {
    const [, uiAlertActions] = useUiAlerts();

    return useCallback(
        (errorObject) => {
            uiAlertActions.createAlert(errorAlertObject);

            // eslint-disable-next-line no-console
            console.error('useServerError Error', errorObject.responseBody || errorObject);
        },
        [uiAlertActions]
    );
}

/**
 * @typedef {Object.<string, string>} FieldMapperDescriptor
 * @typedef {(key: string) => string | null | false} FieldMapperCallback
 * @typedef {FieldMapperDescriptor | FieldMapperCallback} FieldMapper
 */

/**
 *
 * @param {Error} errorObject
 * @param {(fieldName: string, error: { message: string }) => void} setError
 * @param {FieldMapper} [validationFieldMapper]
 */
export function handleSaveError(errorObject, setError, validationFieldMapper) {
    if (errorObject.getStatus && errorObject.getStatus() === HTTP_STATUS_400) {
        const validationErrors = unpackValidationErrors(errorObject.responseBody);

        _.each(validationErrors, (value, key) => {
            const fieldKey = isFunction(validationFieldMapper)
                ? validationFieldMapper(key)
                : _.get(validationFieldMapper, key, key);
            // MDL - For encryption type fields, the field name and the value are different, so create a mapping
            //  AND, sometimes the API sends down duplicate errors for the same field like:
            //  agentWebUI and agentWebUI.username, if we create errors for both, neither will show up
            if (_.isString(fieldKey)) {
                setError(fieldKey, {
                    type: MANUAL_ERROR_TYPE,
                    message: value.message,
                });
            }
        });
    }

    throw errorObject;
}
